/**
 * Run map screen — the branching node DAG.
 *
 * Renders the act's layered graph: rows top-to-bottom, nodes positioned by
 * (row, col), SVG edges between connected nodes, and the boss at the bottom.
 * Available next nodes glow + are clickable; visited/current are marked. The
 * Trace isn't shown here (it's combat-scoped); the topbar carries vitals.
 */

import { el, div, button } from "../dom";
import { topbar } from "../widgets";
import { nodeGlyph, nodeName } from "../glyphs";
import { availableNodes } from "../../engine";
import type { Store } from "../store";
import type { MapNode, MapState } from "../../engine/types";

/** Keyboard focus index into the available nodes (-1 = none). */
let focusNode = -1;

export function resetMapFocus(): void {
  focusNode = -1;
}

const COL_W = 150;
const ROW_H = 70;
const PAD_X = 60;
const PAD_Y = 40;

export function renderMap(store: Store): HTMLElement {
  const run = store.run!;
  const map = run.map;
  const available = availableNodes(map);
  const availIds = new Set(available.map((n) => n.id));

  const screen = div("screen");
  screen.appendChild(topbar(run, () => store.open("deck")));

  const body = div("screen__body");

  // Header row: act title + deck/menu buttons.
  body.appendChild(
    div("row", [
      el("div", {
        class: "h-sub",
        text: `ACT ${map.act} — ${actName(map.act)}`,
      }),
      div("fill"),
      button("btn btn--sm", "▤ DECK", () => store.open("deck")),
      button("btn btn--sm", "≡ MENU", () => store.open("map-menu")),
    ]),
  );

  // The scrollable map canvas.
  const wrap = div("map-wrap scroll");
  const canvas = buildCanvas(store, map, availIds);
  wrap.appendChild(canvas);
  body.appendChild(wrap);

  screen.appendChild(body);
  screen.appendChild(
    div("keyhints", [
      keyhint("←→", "choose path"),
      keyhint("Enter", "enter node"),
      keyhint("V", "deck"),
      keyhint("Esc", "menu"),
    ]),
  );

  // Auto-scroll to current/available region after mount.
  requestAnimationFrame(() => scrollToFront(wrap, map, availIds));

  return screen;
}

function keyhint(k: string, label: string): HTMLElement {
  return el("span", {}, [el("kbd", { text: k }), ` ${label}`]);
}

/** Position helper: x/y center pixel of a node. */
function nodePos(n: MapNode, maxCols: number): { x: number; y: number } {
  // Center each row's columns within the widest row for tidy alignment.
  const x = PAD_X + n.col * COL_W + ((maxCols - colsInRow(n)) * COL_W) / 2;
  const y = PAD_Y + n.row * ROW_H;
  return { x, y };
}

// We need cols-per-row; compute lazily via a closure injected at build time.
let _colsByRow: Record<number, number> = {};
function colsInRow(n: MapNode): number {
  return _colsByRow[n.row] ?? 1;
}

function buildCanvas(
  store: Store,
  map: MapState,
  availIds: Set<string>,
): HTMLElement {
  const nodes = Object.values(map.nodes);
  // Compute cols per row + max cols for layout.
  _colsByRow = {};
  let maxCols = 1;
  for (const n of nodes) {
    _colsByRow[n.row] = Math.max(_colsByRow[n.row] ?? 0, n.col + 1);
  }
  for (const c of Object.values(_colsByRow)) maxCols = Math.max(maxCols, c);

  const rows = map.rows;
  const width = PAD_X * 2 + maxCols * COL_W;
  const height = PAD_Y * 2 + rows * ROW_H;

  const canvas = div("map-canvas");
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;

  // SVG edges (drawn under nodes).
  const svgNS = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(svgNS, "svg");
  svg.setAttribute("class", "map-svg");
  svg.setAttribute("width", String(width));
  svg.setAttribute("height", String(height));
  for (const n of nodes) {
    const from = nodePos(n, maxCols);
    for (const nextId of n.next) {
      const tgt = map.nodes[nextId];
      if (!tgt) continue;
      const to = nodePos(tgt, maxCols);
      const line = document.createElementNS(svgNS, "line");
      line.setAttribute("x1", String(from.x));
      line.setAttribute("y1", String(from.y));
      line.setAttribute("x2", String(to.x));
      line.setAttribute("y2", String(to.y));
      const onPath =
        map.visited.includes(n.id) || availIds.has(nextId) || availIds.has(n.id);
      line.setAttribute("stroke", onPath ? "#3f7d5a" : "#18402b");
      line.setAttribute("stroke-width", onPath ? "3" : "2");
      svg.appendChild(line);
    }
  }
  canvas.appendChild(svg);

  // Available list (stable order) for keyboard focus mapping.
  const availList = [...availIds];

  // Node buttons.
  for (const n of nodes) {
    const pos = nodePos(n, maxCols);
    const isCurrent = map.currentNodeId === n.id;
    const isVisited = map.visited.includes(n.id);
    const isAvail = availIds.has(n.id);
    let cls = `mapnode ${n.kind}`;
    if (isCurrent) cls += " current";
    else if (isAvail) cls += " available";
    else if (isVisited) cls += " visited";
    if (isAvail && availList[focusNode] === n.id) cls += " is-focus";

    const node = div(cls, [nodeGlyph(n.kind)]);
    node.style.left = `${pos.x}px`;
    node.style.top = `${pos.y}px`;
    node.title = nodeName(n.kind);
    node.setAttribute("data-node", n.id);
    if (isAvail) {
      node.addEventListener("click", () => store.chooseNode(n.id));
    }
    canvas.appendChild(node);
  }

  return canvas;
}

function scrollToFront(
  wrap: HTMLElement,
  map: MapState,
  availIds: Set<string>,
): void {
  // Scroll so the current row (or first available) is comfortably visible.
  let targetRow = 0;
  if (map.currentNodeId && map.nodes[map.currentNodeId]) {
    targetRow = map.nodes[map.currentNodeId]!.row;
  } else if (availIds.size > 0) {
    const first = map.nodes[[...availIds][0]!];
    if (first) targetRow = first.row;
  }
  const y = PAD_Y + targetRow * ROW_H - wrap.clientHeight / 2;
  wrap.scrollTop = Math.max(0, y);
}

function actName(act: number): string {
  return act === 1 ? "THE PERIMETER" : act === 2 ? "DEEP NET" : "THE CORE";
}

/** Keyboard handler for the map. Returns true if handled. */
export function mapKey(store: Store, key: string): boolean {
  const run = store.run;
  if (!run || run.phase !== "map") return false;
  const avail = availableNodes(run.map);
  if (avail.length === 0) return false;
  const k = key.toLowerCase();
  if (key === "ArrowRight") {
    focusNode = Math.min(avail.length - 1, focusNode + 1);
    if (focusNode < 0) focusNode = 0;
    store.requestRender();
    return true;
  }
  if (key === "ArrowLeft") {
    focusNode = Math.max(0, focusNode - 1);
    store.requestRender();
    return true;
  }
  if (key === "Enter") {
    const idx = focusNode < 0 ? 0 : focusNode;
    const node = avail[idx];
    if (node) {
      focusNode = -1;
      store.chooseNode(node.id);
      return true;
    }
  }
  if (k === "v") {
    store.open("deck");
    return true;
  }
  return false;
}
